/**
 * RaidSystem — Village raid mechanic.
 *
 * Flow:
 * 1. Player kills a Pillager captain → gets BAD_OMEN effect
 * 2. Player enters village proximity with BAD_OMEN → raid starts
 * 3. 3 waves of enemies spawn around village
 * 4. Defeat all enemies → HERO_OF_VILLAGE effect (trade discounts)
 */

export class RaidSystem {
  constructor() {
    this.active = false;
    this.wave = 0;
    this.maxWaves = 3;
    this.villaPos = null;
    this.mobsRemaining = 0;
    this._waveTimer = 0;
    this._betweenWaves = false;

    // Callbacks set by main.js
    this.onSpawnMobs = null;   // (type, count, center) => spawned count
    this.onShowMessage = null; // (msg) => void
    this.potionSystem = null;

    // UI
    this._raidBar = null;
    this._raidText = null;
    this._buildUI();
  }

  _buildUI() {
    this._raidBar = document.createElement('div');
    this._raidBar.style.cssText = `
      position: fixed;
      top: 40px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0,0,0,0.7);
      border: 2px solid #8B0000;
      padding: 6px 20px;
      font-family: 'Courier New', monospace;
      color: #FF4444;
      font-size: 16px;
      font-weight: bold;
      text-shadow: 1px 1px 0 #000;
      z-index: 200;
      display: none;
      text-align: center;
      min-width: 200px;
    `;
    this._raidText = document.createElement('span');
    this._raidBar.appendChild(this._raidText);
    document.body.appendChild(this._raidBar);
  }

  /**
   * Check if raid should trigger.
   * @param {THREE.Vector3} playerPos
   * @param {Array<{x,y,z}>} villaPositions - village centers
   */
  checkTrigger(playerPos, villaPositions) {
    if (this.active) return;
    if (!this.potionSystem || !this.potionSystem.hasEffect('BAD_OMEN')) return;

    const TRIGGER_RADIUS = 40;
    for (const villa of villaPositions) {
      const dx = playerPos.x - villa.x;
      const dz = playerPos.z - villa.z;
      if (dx * dx + dz * dz < TRIGGER_RADIUS * TRIGGER_RADIUS) {
        this._startRaid(villa);
        return;
      }
    }
  }

  _startRaid(villaPos) {
    this.active = true;
    this.wave = 0;
    this.villaPos = villaPos;
    this.mobsRemaining = 0;
    this._betweenWaves = false;

    // Remove BAD_OMEN
    if (this.potionSystem) {
      this.potionSystem.removeEffect('BAD_OMEN');
    }

    if (this.onShowMessage) this.onShowMessage('¡RAID! ¡La aldea está bajo ataque!');
    if (this.onPlayVideo) this.onPlayVideo('/raid.webm', 3);
    this._raidBar.style.display = 'block';

    this._spawnNextWave();
  }

  _spawnNextWave() {
    this.wave++;
    if (this.wave > this.maxWaves) {
      this._onRaidComplete();
      return;
    }

    // Wave composition
    let pillagers = 0, witches = 0;
    switch (this.wave) {
      case 1: pillagers = 4; witches = 0; break;
      case 2: pillagers = 6; witches = 1; break;
      case 3: pillagers = 8; witches = 2; break;
    }

    let spawned = 0;
    if (this.onSpawnMobs) {
      spawned += this.onSpawnMobs('PILLAGER', pillagers, this.villaPos);
      spawned += this.onSpawnMobs('WITCH', witches, this.villaPos);
    }

    this.mobsRemaining = spawned;
    this._updateUI();

    if (this.onShowMessage) {
      this.onShowMessage(`Oleada ${this.wave}/${this.maxWaves} — ${this.mobsRemaining} enemigos`);
    }
  }

  /**
   * Called when a raid mob is killed.
   */
  onRaidMobKilled() {
    if (!this.active) return;
    this.mobsRemaining = Math.max(0, this.mobsRemaining - 1);
    this._updateUI();

    if (this.mobsRemaining <= 0) {
      if (this.wave >= this.maxWaves) {
        this._onRaidComplete();
      } else {
        // Brief pause between waves
        this._betweenWaves = true;
        this._waveTimer = 5; // 5 seconds between waves
        if (this.onShowMessage) this.onShowMessage(`Oleada ${this.wave} completada. Siguiente en 5s...`);
      }
    }
  }

  _onRaidComplete() {
    this.active = false;
    this._raidBar.style.display = 'none';

    // Grant HERO_OF_VILLAGE effect (40 minutes)
    if (this.potionSystem) {
      this.potionSystem.applyEffect('HERO_OF_VILLAGE', 2400);
    }

    if (this.onShowMessage) this.onShowMessage('¡RAID completada! Eres el Héroe de la Aldea');
  }

  _updateUI() {
    if (!this.active) return;
    this._raidText.textContent = `Raid — Oleada ${this.wave}/${this.maxWaves} — ${this.mobsRemaining} restantes`;
  }

  update(dt) {
    if (!this.active) return;

    if (this._betweenWaves) {
      this._waveTimer -= dt;
      if (this._waveTimer <= 0) {
        this._betweenWaves = false;
        this._spawnNextWave();
      }
    }
  }
}
